import type { GroupLifeTarifacaoLife } from '@/services/group-life';
import { roundHalfUp } from '@/shared/utils/round-half-up.util';

/**
 * Primeiro valor REALMENTE preenchido de uma lista.
 *
 * `??` não serve aqui: o tarifado devolve **zero**, não nulo, nos campos que
 * não se aplicam à cobertura — e `0 ?? x` para em 0. Era o que fazia a coluna
 * "Valor por Segurado" exibir "R$ 0,00" em toda assistência, sem nunca chegar
 * ao campo que tem o preço.
 *
 * Zero legítimo não se perde: quando NENHUM valor é positivo, devolve o
 * primeiro definido (que será o próprio zero).
 */
export function firstMeaningfulValue(
  ...values: Array<number | null | undefined>
): number | undefined {
  const defined = values.filter(
    (value): value is number => value != null && Number.isFinite(value)
  );

  return defined.find(value => value !== 0) ?? defined[0];
}

/**
 * Valor POR VIDA de cada serviço/assistência — o "Valor por Segurado" da aba.
 *
 * Assistência não tem capital: o `SumInsured` dela vem zerado, e por isso a
 * coluna não tinha o que mostrar. O que ela tem é PREÇO, e o preço chega vida a
 * vida nos campos de prêmio do tarifado (`PurePremium`,
 * `PremioLiquido`/`PremioTarifario`, `PremioBruto`/`GrossPremium`,
 * `DuePremium`).
 *
 * A precedência vai do que o segurado efetivamente paga para o mais cru, porque
 * nem toda assistência preenche todos: prêmio devido → bruto → líquido → puro.
 * O valor é usado como vem, sem arredondar nem dividir — é preço fixo da
 * assistência, não um total a ratear.
 */
export function coverageUnitPremium(lives: GroupLifeTarifacaoLife[]) {
  const values = new Map<string, number>();
  for (const life of lives) {
    for (const coverage of life.coverages) {
      const premium = firstMeaningfulValue(
        coverage.duePremium,
        coverage.grossPremium,
        coverage.netPremium,
        coverage.purePremium
      );
      if (premium == null) continue;
      for (const key of [coverage.code, coverage.name]) {
        // A primeira vida com valor POSITIVO manda: uma vida sem direito à
        // assistência entra zerada e não pode definir o preço da tabela.
        const current = values.get(key);
        if (current == null || (current === 0 && premium !== 0)) {
          values.set(key, premium);
        }
      }
    }
  }
  return values;
}

/**
 * Prêmio puro ANUAL de UMA vida de cada serviço/assistência — a base das
 * tabelas de Serviços e Assistências: o "Valor Mensal" é ele ÷ 12
 * ({@link monthlyFromAnnual}) e o "Valor por Segurado", o mensal × vidas
 * ({@link serviceValuePerInsured}).
 *
 * O rating grava o `PurePremium` da assistência como o custo fixo mensal da
 * tabela (`CoverageActuarialMapping.FixedTax`, o valor do Product Memo) × 12 —
 * ex.: Assistência Cesta Natalidade 676008 → 41,21 por ano, 3,43441 por mês —,
 * sem carregamentos nem IOF.
 */
export function coverageUnitPurePremium(lives: GroupLifeTarifacaoLife[]) {
  const values = new Map<string, number>();
  for (const life of lives) {
    for (const coverage of life.coverages) {
      const purePremium = coverage.purePremium;
      if (purePremium == null || !Number.isFinite(purePremium)) continue;
      for (const key of [coverage.code, coverage.name]) {
        // Mesma regra de `coverageUnitPremium`: a primeira vida com valor
        // POSITIVO manda; vida sem a assistência entra zerada.
        const current = values.get(key);
        if (current == null || (current === 0 && purePremium !== 0)) {
          values.set(key, purePremium);
        }
      }
    }
  }
  return values;
}

/**
 * "Valor Mensal" de um serviço/assistência a partir do prêmio ANUAL da vida:
 * ÷ 12, arredondado meio para cima em 2 casas. O custo do Product Memo tem mais
 * casas que a moeda (ex.: 3,43441), e é este valor já arredondado que entra
 * nas contas seguintes ({@link serviceValuePerInsured}). `undefined` passa
 * adiante.
 */
export function monthlyFromAnnual(
  annual: number | undefined
): number | undefined {
  return annual == null ? undefined : roundHalfUp(annual / 12);
}

/**
 * "Valor por Segurado" da tabela de Serviços e Assistências (inclusive
 * capitalização) nas análises: o valor MENSAL arredondado × vidas da cotação,
 * com o resultado também arredondado meio para cima em 2 casas.
 *
 * Sem valor mensal, ou sem a contagem de vidas ainda carregada, devolve
 * `undefined` — a linha mostra "—" em vez de um número errado por um instante.
 */
export function serviceValuePerInsured(
  monthly: number | undefined,
  livesCount: number
): number | undefined {
  if (monthly == null || !(livesCount > 0)) return undefined;
  return roundHalfUp(roundHalfUp(monthly) * livesCount);
}
